import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { configApi } from '../../api/config.api';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Save, Smartphone } from 'lucide-react';

interface MobileVersionConfig {
    id?: number;
    platform: 'android' | 'ios';
    min_version: string;
    latest_version: string;
    store_url: string;
    force_update: boolean;
    title: string;
    message: string;
    is_active: boolean;
}

const MobileConfigData = ({ platform }: { platform: 'android' | 'ios' }) => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState<MobileVersionConfig>({
        platform,
        min_version: '',
        latest_version: '',
        store_url: '',
        force_update: false,
        title: '',
        message: '',
        is_active: true
    });

    const loadConfig = useCallback(async () => {
        try {
            setLoading(true);
            const data = await configApi.getMobileVersion(platform);
            if (data && data.platform) {
                setConfig(data);
            }
        } catch (error) {
            console.error(error);
            toast({ title: "Erreur", description: "Impossible de charger la configuration", type: "error" });
        } finally {
            setLoading(false);
        }
    }, [platform, toast]);

    useEffect(() => {
        loadConfig();
    }, [platform, loadConfig]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const saved = await configApi.setMobileVersion({ ...config, platform });
            setConfig(saved);
            toast({ title: "Succès", description: "Configuration sauvegardée", type: "success" });
        } catch (error) {
            console.error(error);
            toast({ title: "Erreur", description: "Échec de la sauvegarde", type: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    }

    return (
        <div className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label>Dernière Version (Latest)</Label>
                    <Input 
                        value={config.latest_version} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfig({ ...config, latest_version: e.target.value })}
                        placeholder="ex: 1.0.5" 
                    />
                    <p className="text-xs text-muted-foreground">Version suggérée aux utilisateurs.</p>
                </div>

                <div className="space-y-2">
                    <Label>Version Minimum (Min)</Label>
                    <Input 
                        value={config.min_version} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfig({ ...config, min_version: e.target.value })}
                        placeholder="ex: 1.0.0" 
                    />
                    <p className="text-xs text-muted-foreground">Les versions inférieures seront bloquées.</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                    <Label>URL du Store</Label>
                    <Input 
                        value={config.store_url} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfig({ ...config, store_url: e.target.value })}
                        placeholder="https://play.google.com/..." 
                    />
                </div>

                <div className="space-y-2">
                    <Label>Titre du Message</Label>
                    <Input 
                        value={config.title} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfig({ ...config, title: e.target.value })}
                    />
                </div>

                <div className="space-y-2 md:col-span-2">
                    <Label>Message de mise à jour</Label>
                    <Textarea 
                        value={config.message} 
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setConfig({ ...config, message: e.target.value })}
                        placeholder="Une nouvelle version est disponible..." 
                    />
                </div>
            </div>

            <div className="flex items-center space-x-4 border p-4 rounded-lg bg-slate-50">
                <Switch 
                    checked={config.force_update} 
                    onCheckedChange={(checked: boolean) => setConfig({ ...config, force_update: checked })} 
                />
                <div className="flex-1">
                    <Label className="font-bold">Forcer la mise à jour</Label>
                    <p className="text-xs text-muted-foreground">
                        Si activé, le blocage "Force Update" sera appliqué même si la version est {'>='} Min Version (utile pour maintenance critique).
                    </p>
                </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Save className="mr-2 h-4 w-4" /> Sauvegarder
            </Button>
        </div>
    );
};

export default function MobileConfig() {
    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-slate-800">Configuration Mobile</h2>
                    <p className="text-muted-foreground">Gérez les versions et mises à jour de l'application mobile.</p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Smartphone className="h-5 w-5 text-blue-600" />
                        Gestion des Versions
                    </CardTitle>
                    <CardDescription>Configurez les versions minimales et actuelles pour Android et iOS.</CardDescription>
                </CardHeader>
                <CardContent>
                    <Tabs defaultValue="android">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="android">Android</TabsTrigger>
                            <TabsTrigger value="ios">iOS</TabsTrigger>
                        </TabsList>
                        <TabsContent value="android">
                            <MobileConfigData platform="android" />
                        </TabsContent>
                        <TabsContent value="ios">
                            <MobileConfigData platform="ios" />
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    );
}
